/**
 * Bounding what command and file output crosses to the model provider
 * (17.08.2026).
 *
 * `condenseToolResultForApi` in AIChatPanel.js is where the app decides what
 * leaves the machine. Most tools are already tightly minimised there —
 * `send_request` withholds the response body entirely, `db_run_sql` never
 * feeds rows back, and every variable path replaces a secret value with
 * `<SECRET>` at the source. Two tools fell through to the raw default and sent
 * their whole result verbatim:
 *
 *   terminal_run_command — the complete stdout/stderr of a command run over
 *   SSH on the user's own server. Server logs routinely contain IP addresses,
 *   email addresses and user ids, so `tail -n 2000 /var/log/app.log` shipped
 *   personal data to a third-country processor, unbounded and unannounced.
 *
 *   read_document — the full text of a note, which is whatever the user wrote
 *   in it.
 *
 * Unbounded is also a correctness problem independent of privacy: one `cat` of
 * a large file can consume the whole context window and cost the user real
 * money on their own API key.
 *
 * HEAD AND TAIL, not a prefix: a failing command puts its context at the top
 * and its error at the bottom, and a prefix-only clamp throws away the half
 * that says what went wrong.
 */

/** Default ceiling per output field. Roughly 2k tokens — enough to diagnose. */
export const DEFAULT_OUTPUT_BUDGET = 8000;

/**
 * Clamp `text` to `budget` characters, keeping the head and the tail.
 *
 * Returns the original string unchanged when it already fits, so a short
 * result is never rewritten and never gains a marker.
 */
export function clampOutput(text, budget = DEFAULT_OUTPUT_BUDGET) {
  if (typeof text !== 'string' || text.length <= budget) return text;

  // Two-thirds head, one-third tail: the tail is where the error usually is,
  // but the head carries what was being attempted.
  const headLen = Math.floor((budget * 2) / 3);
  const tailLen = budget - headLen;
  // `slice(-0)` is `slice(0)` — the WHOLE string — so a zero or negative budget
  // returned the complete text wearing a "truncated" marker, which is the most
  // exposure possible while claiming the least. Head-only is the safe read.
  if (tailLen <= 0) return text.slice(0, Math.max(0, headLen));
  const omitted = text.length - budget;

  return `${text.slice(0, headLen)}\n\n… [${omitted} characters omitted — full output is in the app] …\n\n${text.slice(-tailLen)}`;
}

/**
 * Condense an SSH command result for the provider.
 *
 * The user still sees the complete output: Rust mirrors the command and its
 * output into the terminal tab for that connection (ssh-term-ai-exec), so
 * clamping here costs the user nothing and only bounds what is transmitted.
 */
export function condenseTerminalResult(result, budget = DEFAULT_OUTPUT_BUDGET) {
  const stdout = clampOutput(result?.stdout ?? '', budget);
  const stderr = clampOutput(result?.stderr ?? '', budget);

  return {
    stdout,
    stderr,
    exit_code: result?.exit_code,
    // Flagged explicitly so the model knows it is reasoning about a partial
    // view and can ask for a narrower command instead of guessing.
    output_truncated:
      stdout !== (result?.stdout ?? '') || stderr !== (result?.stderr ?? '') || undefined,
  };
}

/** Condense a document read: keep the identifying fields, clamp the content. */
export function condenseDocumentResult(result, budget = DEFAULT_OUTPUT_BUDGET) {
  if (!result || typeof result !== 'object' || result.error) return result;

  // The content field has varied across document revisions; clamp whichever
  // one this result actually carries rather than assuming a single shape.
  const key = ['content', 'text', 'markdown', 'body'].find(
    (k) => typeof result[k] === 'string'
  );
  if (!key) return result;

  const clamped = clampOutput(result[key], budget);
  if (clamped === result[key]) return result;

  return { ...result, [key]: clamped, content_truncated: true };
}
